'use client';

import { datadogRum, type DurationVitalOptions, type RumInitConfiguration } from '@datadog/browser-rum';

import { nextjsPlugin } from '@datadog/browser-rum-nextjs';

import { getCookieValue } from '@/utils/cookies/client';
import { COOKIES } from '@/utils/cookies';
import { getAppPlatform } from '@/logic/platform';

import type { TRumAction, TRumError } from './types';

import { flushPendingHydrationTiming, reportHydrationMeasured } from './hydration-rum-timing';

let isDatadogInitialized = false;
const isDevelopment = process.env.NODE_ENV === 'development';

type DurationVitalReference = ReturnType<typeof datadogRum.startDurationVital>;

const activeVitals = new Map<string, DurationVitalReference>();

const setBrowserStackConditions = () => {
    const networkProfile = getCookieValue(COOKIES.BROWSER_STACK_NETWORK_PROFILE);
    if (networkProfile?.length) datadogRum.setGlobalContextProperty('network-profile', networkProfile.toUpperCase());

    const devicePerformance = getCookieValue(COOKIES.BROWSER_STACK_DEVICE_PERFORMANCE);
    if (devicePerformance?.length)
        datadogRum.setGlobalContextProperty('device-performance', devicePerformance.toUpperCase());

    const automationTag = getCookieValue(COOKIES.BROWSER_STACK_AUTOMATION_TAG);
    if (automationTag?.length) datadogRum.setGlobalContextProperty('automation-tag', automationTag);
};

export const ddSetUser = (userId: string) => {
    datadogRum.setUser({
        id: userId
    });
};

export const ddClearUser = () => {
    datadogRum.clearUser();
};

export const initialize = (config: RumInitConfiguration) => {
    if (isDatadogInitialized) {
        // TODO change me so i will be init only one time...
        console.warn('[Datadog] Already init');
        return;
    }

    datadogRum.init({
        ...config,
        plugins: [...(config.plugins ?? []), nextjsPlugin()]
    });

    datadogRum.setGlobalContextProperty('platform', getAppPlatform().toUpperCase());
    datadogRum.setGlobalContextProperty('brand', process.env.BRAND!.toUpperCase());
    datadogRum.setGlobalContextProperty('region', getCookieValue(COOKIES.REGION).toUpperCase());
    datadogRum.setGlobalContextProperty('product', process.env.PRODUCT!.toUpperCase());
    datadogRum.setGlobalContextProperty('next_user', true);

    setBrowserStackConditions();

    isDatadogInitialized = true;

    flushPendingHydrationTiming((name, durationMs) => datadogRum.addTiming(name, durationMs), true);
};

export const ddReportHydrationMeasured = (durationMs: number) => {
    reportHydrationMeasured(durationMs, (name, ms) => datadogRum.addTiming(name, ms), isDatadogInitialized);
};

export const ddRumAction = (action: TRumAction, debug = isDevelopment) => {
    // Log to console in development
    if (debug && isDevelopment) {
        const { name, description, context, use_case } = action;
        console.log(`DD RUM ACTION [${name}]: ${description} - `, { context, use_case });
    }

    const { name, description, context, use_case } = action;
    datadogRum.addAction(name, { description, ...context, use_case });
};

export const ddRumTiming = (name: string, time?: number) => {
    datadogRum.addTiming(name, time);
};

const reportedTimings = new Set<string>();

/**
 * Like ddRumTiming, but emits a given timing name at most once per RUM view.
 * Use for one-shot page-relative timings (e.g. component-rendered, data-ready)
 * where re-emission on refetch or remount would skew P90/P99.
 *
 * Reset between views by calling resetRumTimingsForNewView() on route change.
 */
export const ddRumTimingOnce = (name: string, time?: number) => {
    if (reportedTimings.has(name)) {
        return;
    }
    reportedTimings.add(name);
    datadogRum.addTiming(name, time);
};

export const resetRumTimingsForNewView = () => {
    reportedTimings.clear();
};

type DdStartVitalOpts = {
    replace?: boolean;
};

export const ddStartVital = (name: string, options?: DurationVitalOptions, opts: DdStartVitalOpts = {}) => {
    if (activeVitals.has(name)) {
        if (!opts.replace) {
            return;
        }
        activeVitals.delete(name);
    }
    const reference = datadogRum.startDurationVital(name, options);
    activeVitals.set(name, reference);
};

export const ddStopVital = (name: string, options?: DurationVitalOptions) => {
    const reference = activeVitals.get(name);
    if (!reference) {
        return;
    }
    activeVitals.delete(name);
    datadogRum.stopDurationVital(reference, options);
};

/**
 * Silently cancels an active duration vital without reporting it to Datadog.
 */
export const ddCancelVital = (name: string) => {
    activeVitals.delete(name);
};

export const ddRumError = (error: TRumError, debug = isDevelopment) => {
    // Log to console in development
    if (debug && isDevelopment) {
        const { name, description, use_case } = error;
        console.log(`DD RUM ERROR [${name}]: ${description} - `, { error, use_case });
    }

    if (isDevelopment) {
        console.warn(`[Datadog] Attempted to send error before initialization: ${error.name}`);
    }

    const { name, description, context, stack, use_case } = error;
    datadogRum.addError({ name, message: description, stack }, { ...context, use_case });
};
